/**
 * Shared logout utility to ensure consistent Firebase Auth cleanup across the application
 */

import { signOut as nextAuthSignOut } from "next-auth/react";
import { auth } from "@/lib/firebase";
import { signOut as firebaseSignOut } from "firebase/auth";

/**
 * Performs a complete logout by clearing Firebase Auth, client data, and NextAuth session
 * 
 * CRITICAL: This function ensures complete session cleanup to prevent session leakage
 * between different users on the same browser.
 * 
 * @param callbackUrl - URL to redirect to after logout (default: "/login")
 * @returns Promise that resolves when logout is complete
 */
export async function performLogout(callbackUrl = "/login") {
  // ALWAYS call Firebase signOut first
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error("Firebase signOut error:", error);
    // Continue logout even if Firebase signOut fails
  }
  
  // CRITICAL: Clear ALL client-side state
  // This prevents stale auth state from being used by the next user
  try {
    if (typeof window !== 'undefined') {
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear IndexedDB databases
      const idb = window.indexedDB;
      if (idb) {
        if ('databases' in idb && typeof idb.databases === 'function') {
          try {
            const databases = await idb.databases();
            for (const db of databases) {
              if (db.name) {
                idb.deleteDatabase(db.name);
              }
            }
          } catch (err) {
            console.warn('Failed to enumerate IndexedDB databases:', err);
            // Try to delete known Firebase databases
            idb.deleteDatabase('firebaseLocalStorageDb');
          }
        } else {
          // Fallback: delete known Firebase database when databases() is not available
          idb.deleteDatabase('firebaseLocalStorageDb');
        }
      }
    }
  } catch (error) {
    console.error("Error clearing client data:", error);
    // Continue logout even if clearing fails
  }
  
  // NextAuth signOut (clears session cookies) and redirect
  await nextAuthSignOut({ callbackUrl });
}
